import { useState } from 'react';
import type { Api, Statement } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { DangerButton, Field, ReadOnlyNote, SaveBar, Section, useDraft } from '../ui';
import { errorText } from './AgentSettings';

type Row = { id: string; topic: string; text: string };

/**
 * Knowledge tab (ADR-021/022): where the contact came from (required for agents that call), the business's
 * own answers to predictable questions (one row each, edited in place).
 */
export const KnowledgePanel = ({ api, t, agentId, initial, needsSource }: { api: Api; t: T; agentId: string; initial: Statement[]; needsSource: boolean }) => {
  // ADR-071: without the agents permission the answers are shown read-only (no add, edit or remove).
  const canEdit = useCan()(PERM.agents);
  const d = useDraft({
    source: initial.find((x) => x.id === 'contact_source')?.text ?? '',
    rows: initial.filter((x) => x.id !== 'contact_source') as Row[],
  });
  const v = d.value;
  const [editing, setEditing] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setRow = (i: number, patch: Partial<Row>) => d.set({ ...v, rows: v.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const rows = v.rows
        .filter((x) => x.topic.trim() && x.text.trim())
        .map((x, i) => ({ id: /^[a-z][a-z0-9_]*$/u.test(x.id) ? x.id : `answer_${i + 1}`, topic: x.topic.trim(), text: x.text.trim() }));
      await api.saveStatements(agentId, [...(v.source.trim() ? [{ id: 'contact_source', topic: 'contact_source', text: v.source.trim() }] : []), ...rows]);
      d.commit();
      setEditing(null);
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings">
      <fieldset disabled={!canEdit} className="readonly-fieldset">
        <ReadOnlyNote t={t} need={PERM.agents} />
        <Section title={t('statements.contactSource')} description={t(needsSource ? 'knowledge.source.hint.required' : 'knowledge.source.hint')}>
          <Field label={t('knowledge.source.label')}>
            <textarea rows={2} value={v.source} placeholder={t('statements.contactSource.placeholder')} onChange={(e) => d.set({ ...v, source: e.target.value })} />
          </Field>
        </Section>
        <Section title={t('statements.title')} description={t('statements.hint')}>
          {canEdit && (
            <div>
              <button type="button" className="secondary" onClick={() => { d.set({ ...v, rows: [{ id: '', topic: '', text: '' }, ...v.rows] }); setEditing(0); }}>+ {t('statements.add')}</button>
            </div>
          )}
          {v.rows.length === 0 && <div className="empty"><p>{t('knowledge.empty')}</p></div>}
          {v.rows.length > 0 && (
            <div className="list">
              {v.rows.map((r, i) =>
                editing === i ? (
                  <div key={i} className="list-row">
                    <div className="statement-edit">
                      <input value={r.topic} autoFocus placeholder={t('statements.topic')} onChange={(e) => setRow(i, { topic: e.target.value })} />
                      <textarea rows={2} value={r.text} placeholder={t('statements.answer')} onChange={(e) => setRow(i, { text: e.target.value })} />
                      <div className="row"><button type="button" className="ghost" onClick={() => setEditing(null)}>{t('knowledge.done')}</button></div>
                    </div>
                  </div>
                ) : (
                  <div key={i} className={canEdit ? 'list-row clickable statement-view' : 'list-row statement-view'} style={canEdit ? undefined : { cursor: 'default' }} onClick={canEdit ? () => setEditing(i) : undefined}>
                    <div className="grow">
                      <div className="title">{r.topic || <span className="muted">{t('statements.topic')}</span>}</div>
                      <div className="sub">{r.text || '—'}</div>
                    </div>
                    <span onClick={(e) => e.stopPropagation()}>
                      <DangerButton t={t} need={PERM.agents} label={t('statements.remove')} onConfirm={() => { d.set({ ...v, rows: v.rows.filter((_, j) => j !== i) }); setEditing(null); }} />
                    </span>
                  </div>
                ),
              )}
            </div>
          )}
        </Section>
      </fieldset>
      <SaveBar t={t} need={PERM.agents} dirty={d.dirty} saving={saving} error={error} onReset={() => { d.reset(); setEditing(null); }} onSave={() => void save()} />
    </div>
  );
};
